import React from "react";
import { ExternalLink, ShoppingCart, X } from "lucide-react";
import IpfsImage from "./IpfsImage";

const HERO_VIDEO_IPFS = process.env.REACT_APP_HERO_VIDEO_CID || "bafybeifjtqzoar2cz7vuqo5ldsid4kkmam5yzi6odr5kjp5gmpqzoybune";
const HERO_POSTER_IPFS = process.env.REACT_APP_HERO_POSTER_CID || "bafkreibc4qkdcbkxg4ajn6434iatsz3e54fm2vab4czixtb6hdukisecsy";
const VIDEO_GATEWAYS = [`https://nomelupin.mypinata.cloud/ipfs/${HERO_VIDEO_IPFS}`];
const POSTER_URL = `https://nomelupin.mypinata.cloud/ipfs/${HERO_POSTER_IPFS}`;

const renderRichText = (text) => {
    if (!text) return null;
    const pattern = /(https?:\/\/[^\s]+|t\.me\/[a-zA-Z0-9_]+|@[a-zA-Z0-9_]{3,32})/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        const token = match[0];
        let href = token;
        let label = token;
        if (token.startsWith("@")) {
            href = `https://t.me/${token.slice(1)}`;
        } else if (token.startsWith("t.me/")) {
            href = `https://${token}`;
            label = `@${token.slice(5)}`;
        }
        const isHandle = token.startsWith("@") || token.startsWith("t.me/");
        if (isHandle) {
            parts.push(
                <a key={`l-${key++}`} href={href} target="_blank" rel="noreferrer"
                   className="inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full bg-[#7c3aed]/20 border border-[#7c3aed]/50 text-[#e879f9] hover:bg-[#e879f9]/20 hover:border-[#e879f9] hover:text-white transition-colors font-mono text-xs font-semibold align-middle">
                    {label}
                </a>
            );
        } else {
            parts.push(
                <a key={`l-${key++}`} href={href} target="_blank" rel="noreferrer"
                   className="text-[#e879f9] underline decoration-dotted underline-offset-2 hover:text-[#f0abfc] transition-colors break-all">
                    {token}
                </a>
            );
        }
        lastIndex = match.index + token.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
};

const InfoDialog = ({ open, onClose, info, mode = "all" }) => {
    if (!open) return null;
    const description = info?.description || "";
    const links = info?.links || [];
    const showDescription = mode === "all" || mode === "info";
    const showLinks = mode === "all" || mode === "links";
    const showCta = mode === "all";

    return (
        <div
            className="fixed inset-0 z-[100] bg-[#0d0d0d] overflow-y-auto"
            data-testid="info-splash"
        >
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white"
                data-testid="info-splash-close"
                aria-label="Chiudi"
            >
                <X className="w-5 h-5" />
            </button>

            <div className="max-w-md mx-auto px-5 pt-8 pb-12 flex flex-col items-center text-center">
                {/* Circular video avatar — same logo as hero */}
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-5">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#a78bfa] to-[#5b21b6] shadow-[0_8px_32px_rgba(46,123,191,0.45)]" />
                    <video
                        src={VIDEO_GATEWAYS[0]}
                        poster={POSTER_URL}
                        autoPlay loop muted playsInline preload="auto"
                        className="absolute inset-[7px] rounded-full object-cover w-[calc(100%-14px)] h-[calc(100%-14px)] bg-[#3c1361]"
                        onError={(e) => {
                            const i = VIDEO_GATEWAYS.indexOf(e.currentTarget.src);
                            if (i >= 0 && i < VIDEO_GATEWAYS.length - 1) e.currentTarget.src = VIDEO_GATEWAYS[i + 1];
                        }}
                    />
                </div>

                <h1
                    className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight mb-3"
                    data-testid="info-splash-title"
                >
                    il vero pinko
                </h1>

                {description && showDescription && (
                    <p
                        className="text-white/90 text-base sm:text-lg whitespace-pre-wrap leading-relaxed mb-7 max-w-sm"
                        data-testid="info-splash-description"
                    >
                        {renderRichText(description)}
                    </p>
                )}

                {/* Primary CTA — only on initial splash */}
                {showCta && (
                    <button
                        onClick={onClose}
                        className="w-full inline-flex items-center justify-center gap-3 py-4 px-5 mb-3 rounded-full bg-gradient-to-r from-[#a78bfa] to-[#5b21b6] text-white font-semibold text-lg shadow-lg shadow-[#7c3aed]/40 hover:shadow-xl active:scale-[0.98] transition-all"
                        data-testid="info-splash-menu-cta"
                    >
                        <ShoppingCart className="w-5 h-5" />
                        Menu Catalogo
                    </button>
                )}

                {/* Link list */}
                {showLinks && (
                <div className="w-full flex flex-col gap-2.5 mt-2">
                    {links.map((l) => (
                        <a
                            key={l.id || l.url}
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`info-splash-link-${l.id || l.label}`}
                            className="group flex items-center justify-between gap-3 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 px-5 py-3.5 transition-all active:scale-[0.99]"
                        >
                            {l.icon_url && (
                                <span className="w-8 h-8 rounded-full overflow-hidden border border-white/20 shrink-0 bg-black/40">
                                    <IpfsImage src={l.icon_url} alt="" loading="lazy"
                                         className="w-full h-full object-cover" />
                                </span>
                            )}
                            <span className="flex-1 font-semibold text-white uppercase tracking-wide text-sm sm:text-base truncate">
                                {l.label}
                            </span>
                            <ExternalLink className="w-4 h-4 text-[#a78bfa] shrink-0 group-hover:translate-x-0.5 transition-transform" />
                        </a>
                    ))}
                </div>
                )}

                <button
                    onClick={onClose}
                    className="mt-8 text-sm font-mono text-white/50 hover:text-white underline"
                    data-testid="info-splash-skip"
                >
                    {showCta ? "salta · vai al menu" : "chiudi"}
                </button>
            </div>
        </div>
    );
};

export default InfoDialog;
